<script setup>
    import Layout from "@/layouts/main.vue";
    import PageHeader from "@/components/page-header";  
    import "@suadelabs/vue3-multiselect/dist/vue3-multiselect.css";
    import { required, helpers } from "@vuelidate/validators";
    import useVuelidate from "@vuelidate/core";
    import { computed, ref, reactive, onMounted, onUnmounted } from 'vue';
    import { useStore } from 'vuex';
    import { roleService } from '../../../helpers/authservice/role.service';

    const state = reactive({
        name: '',
        description: '',
        user_type: '',
        is_superadmin: false,
    });

    const userTypes = ref([]);

    const nameSlug = computed(() => {
        const parts = [state.user_type, state.name].filter(Boolean);
        return parts.join(' ').trim().toLowerCase().replace(/\s+/g, '-');
    });

    const rules = computed(() => ({
        name: {
            required: helpers.withMessage("Name is required", required),
        },
        user_type: {
            required: helpers.withMessage("User Type is required", required),
        },
    }));

    const store = useStore();
    const v$ = useVuelidate(rules, state);

    const title = ref("Roles");
    const items = ref([
        {
            text: "Home",
            href: "/",
        },
        {
            text: "User Management",
            href: "/user-management/roles/list",
            active: true
        },
        {
            text: "Add New Role",
            active: true,
        }
    ]);

    const submitted = ref(false);
    const loading = computed(() => store.state.globals.loading);
    const notification = computed(() => store ? store.state.notification : null)

    const createRole = async () => {
        submitted.value = true
        v$.value.$touch();

        if (v$.value.$invalid) return;
        await store.dispatch('role/createRole', state);
    }

    onMounted(async () => {
        try {
            const data = await roleService.getUserTypes();
            userTypes.value = data.data;
        } catch (err) {
            const message = err?.response?.data?.message || 'Failed to load user types';
            store.dispatch('notification/error', message, { root: true });
        }
    })

    onUnmounted(() => {
        store.dispatch('notification/clear', null, { root: true });
    })
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
        <div class="col lg-12">
            <b-overlay :show="loading">
                <div class="card">
                    <div class="card-header">
                        <h4 class="card-title mb-0">Add New Role</h4>
                    </div>
                    <div class="card-body">
                        <div v-if="notification.type === 'danger' && notification.messages.length > 0">
                            <b-alert v-for="(message, index) in notification.messages" :key="index"
                                show variant="danger" class="fade show" role="alert">
                                {{ message }}
                            </b-alert>
                        </div>

                        <div v-if="notification.type === 'success' && notification.messages.length > 0">
                            <b-alert v-for="(message, index) in notification.messages" :key="index"
                                show variant="danger" class="fade show" role="alert">
                                {{ message }}
                            </b-alert>
                        </div>

                        <form @submit.prevent="createRole" class="mx-2">
                            <div class="row">
                                <p class="fs-6 text text-decoration-underline">Role Information</p>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label for="name" class="form-label required">Name</label> 
                                        <input 
                                            autofocus
                                            :class="{ 'is-invalid': submitted && v$.name.$error }"
                                            v-model="state.name" class="form-control" type="text" placeholder="Enter name..." id="name" />
                                        <div class="form-text text-muted">
                                            <i class="mdi mdi-information-outline"></i> Format: <strong>Dept RoleName</strong> &mdash; slug will combine User Type + Name
                                        </div>
                                        <div v-if="nameSlug" class="form-text text-muted">
                                            Slug: <code>{{ nameSlug }}</code>
                                        </div>
                                        <div
                                            v-for="item of v$.name.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label for="description" class="form-label">Description</label>
                                        <textarea
                                            v-model="state.description" class="form-control" placeholder="Enter description..." id="description"></textarea>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label for="userType" class="form-label required">User Type</label>
                                        <select v-model="state.user_type" :class="{ 'is-invalid': submitted && v$.user_type.$error }" class="form-select" id="userType">
                                            <option value="">Select user type...</option>
                                            <option v-for="type in userTypes" :key="type" :value="type">{{ type }}</option>
                                        </select>
                                        <div v-for="item of v$.user_type.$errors" :key="item.$uid" class="invalid-feedback">
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label class="form-label d-block">Super Admin</label>
                                        <div class="form-check form-switch">
                                            <input
                                                class="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="is_superadmin"
                                                v-model="state.is_superadmin" />
                                            <label class="form-check-label" for="is_superadmin">
                                                <span v-if="state.is_superadmin" class="badge bg-success">Super Admin</span>
                                                <span v-else class="text-muted">Grant full access to all LGUs</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="row">
                                <div class="col-lg-12">
                                    <div class="text-end">
                                        <router-link to="/user-management/roles/list" type="submit" class="btn btn-danger mr-3">
                                            <i class="ri-close-fill fs-4 mr-1 valign-middle"></i>
                                            Cancel
                                        </router-link>
                                        <button type="submit" class="btn btn-success">
                                            <i class="ri-save-2-fill fs-4 mr-1 valign-middle"></i>
                                            Add Role
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                </div>
                </div>
            </b-overlay>
        </div>
    </div>
  </Layout>
</template>
